import { jwtDecode } from 'jwt-decode';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type TokenData = {
  iat: number;
  jti: string;
  UserId: string;
  FirstName: string;
  LastName: string;
  UserName: string;
  Email: string;
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
};

type RefreshTokenData = {
  salt: string;
  userId: string;
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  nbf: number;
};

export type AuthStore = {
  token: string | null;
  tokenData: TokenData | null;
  refreshToken: string | null;
  refreshTokenData: RefreshTokenData | null;
  twoFactorTokenExpiresAt: number | null;
  serviceTokenExpiresAt: number | null;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setTwoFactorTokenExpiresAt: (expires: number | null) => void;
  setServiceTokenExpiresAt: (expires: number | null) => void;
  clearTokens: () => void;
};

export interface IColumnsVisibilityStore {
  hiddenColumns: number[];
  toggleColumn: (column: number) => void;
  isColumnHidden: (column: number) => boolean;
  isColumnDisabled: (column: number) => boolean;
}

interface ITableConfig {
  grid: string;
  hiddenColumns: number[];
  columnsNumber?: number;
  minimumColumns?: number;
}

export interface ITablesConfig {
  tablesConfig: Record<string, ITableConfig>;
  changeTableConfig: (id: string, config: Partial<ITableConfig>) => void;
  toggleTableColumn: (tableId: string, column: number) => void;
  isTableColumnHidden: (tableId: string, column: number) => boolean;
  isTableColumnDisabled: (tableId: string, column: number) => boolean;
}

export type IStore = AuthStore & ITablesConfig;

const decodeToken = (token: string): TokenData | null => {
  if (!token) return null;
  return jwtDecode(token);
};

const decodeRefreshToken = (refreshToken: string): RefreshTokenData | null => {
  if (!refreshToken) return null;
  return jwtDecode(refreshToken);
};

export const stateStore = createStore<IStore>()(
  persist(
    (set, get) => ({
      token: null,
      tokenData: null,
      refreshToken: null,
      refreshTokenData: null,
      twoFactorTokenExpiresAt: null,
      serviceTokenExpiresAt: null,
      setToken: (token: string | null) => {
        const decodedToken = decodeToken(token as string);
        const tokenData = (() => {
          try {
            return decodedToken;
          } catch (error) {
            console.error(error);
            return null;
          }
        })();
        set({
          token,
          tokenData,
        });
      },
      setRefreshToken: (refreshToken: string | null) => {
        const decodedRefreshToken = decodeRefreshToken(refreshToken as string);
        const refreshTokenData = (() => {
          try {
            return decodedRefreshToken;
          } catch (error) {
            console.error(error);
            return null;
          }
        })();
        set({
          refreshToken,
          refreshTokenData,
        });
      },
      setTwoFactorTokenExpiresAt: (expires: number | null) => {
        const twoFactorTokenExpiresAt = expires ? Date.now() + expires : 0;
        set({
          twoFactorTokenExpiresAt,
        });
      },
      setServiceTokenExpiresAt: (expires: number | null) => {
        const serviceTokenExpiresAt = expires ? Date.now() + expires : 0;
        set({
          serviceTokenExpiresAt,
        });
      },
      clearTokens: () => {
        set({
          token: null,
          tokenData: null,
          refreshTokenData: null,
          refreshToken: null,
          twoFactorTokenExpiresAt: null,
        });
      },
      tablesConfig: {} as Record<string, ITableConfig>,
      changeTableConfig: (id: string, config: Partial<ITableConfig>) => {
        set(state => ({
          tablesConfig: {
            ...state.tablesConfig,
            [id]: {
              grid: config.grid ?? state.tablesConfig[id]?.grid ?? '',
              hiddenColumns:
                config.hiddenColumns ??
                state.tablesConfig[id]?.hiddenColumns ??
                [],
              columnsNumber:
                config.columnsNumber ?? state.tablesConfig[id]?.columnsNumber,
              minimumColumns:
                config.minimumColumns ??
                state.tablesConfig[id]?.minimumColumns ??
                2,
            },
          },
        }));
      },
      toggleTableColumn: (tableId: string, column: number) => {
        set(state => {
          const tableConfig = state.tablesConfig[tableId];
          if (!tableConfig) return state;

          const isHidden = tableConfig.hiddenColumns.includes(column);
          const columnsNumber = tableConfig.columnsNumber ?? 0;
          const minimumColumns = tableConfig.minimumColumns ?? 2;

          if (
            !isHidden &&
            columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns
          ) {
            return state;
          }

          const updatedHiddenColumns = isHidden
            ? tableConfig.hiddenColumns.filter(c => c !== column)
            : [...tableConfig.hiddenColumns, column];

          return {
            tablesConfig: {
              ...state.tablesConfig,
              [tableId]: {
                ...tableConfig,
                hiddenColumns: updatedHiddenColumns,
              },
            },
          };
        });
      },
      isTableColumnHidden: (tableId: string, column: number) => {
        const tableConfig = get().tablesConfig[tableId];
        return tableConfig?.hiddenColumns.includes(column) ?? false;
      },
      isTableColumnDisabled: (tableId: string, column: number) => {
        const tableConfig = get().tablesConfig[tableId];
        if (!tableConfig) return false;

        const columnsNumber = tableConfig.columnsNumber ?? 0;
        const minimumColumns = tableConfig.minimumColumns ?? 2;
        return (
          columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns &&
          !tableConfig.hiddenColumns.includes(column)
        );
      },
    }),
    {
      name: 'dev-magellan-admin-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export type ExtractState<S> = S extends {
  getState: () => infer T;
}
  ? T
  : never;

type Params<U> = Parameters<typeof useStore<typeof stateStore, U>>;

const tokenSelector = (state: ExtractState<typeof stateStore>) => state.token;
const tokenDataSelector = (state: ExtractState<typeof stateStore>) =>
  state.tokenData;
const refreshTokenDataSelector = (state: ExtractState<typeof stateStore>) =>
  state.refreshTokenData;
const refreshTokenSelector = (state: ExtractState<typeof stateStore>) =>
  state.refreshToken;
const twoFactorTokenExpiresAtSelector = (
  state: ExtractState<typeof stateStore>
) => state.twoFactorTokenExpiresAt;
const actionsSelector = (state: ExtractState<typeof stateStore>) => state;

export const getAccessToken = () => tokenSelector(stateStore.getState());
export const getAccessTokenData = () =>
  tokenDataSelector(stateStore.getState());
export const getRefreshTokenData = () =>
  refreshTokenDataSelector(stateStore.getState());
export const getRefreshToken = () =>
  refreshTokenSelector(stateStore.getState());
export const getTwoFactorTokenExpiresAt = () =>
  twoFactorTokenExpiresAtSelector(stateStore.getState());

export const getActions = () => actionsSelector(stateStore.getState());

const useStateStore = <U>(selector: Params<U>[1]) => {
  return useStore(stateStore, selector);
};

export const useStateStorage = () => useStateStore(stateStore.getState);

export const useToken = () => useStateStore(tokenSelector);
export const useTokenData = () => useStateStore(tokenDataSelector);
export const useRefreshTokenData = () =>
  useStateStore(refreshTokenDataSelector);
export const useRefreshToken = () => useStateStore(refreshTokenSelector);
export const useTwoFactorTokenExpiresAt = () =>
  useStateStore(twoFactorTokenExpiresAtSelector);

export const useActions = () => useStateStore(actionsSelector);

export const useTablesConfig = () => useStateStore(state => state.tablesConfig);
export const useChangeTableConfig = () =>
  useStateStore(state => state.changeTableConfig);
export const useToggleTableColumn = () =>
  useStateStore(state => state.toggleTableColumn);
export const useIsTableColumnHidden = () =>
  useStateStore(state => state.isTableColumnHidden);
export const useIsTableColumnDisabled = () =>
  useStateStore(state => state.isTableColumnDisabled);
